import { Controller } from "@hotwired/stimulus";
import consumer from "../channels/consumer";

/**
 * Handles message composition in conversation view
 * - Form submission and clearing
 * - Real-time message updates via ConversationChannel
 * - Typing indicators (optional)
 */
export default class extends Controller {
  static targets = ["input", "submit", "messages"];
  static values = {
    conversationId: Number
  };

  connect() {
    // Prevent duplicate subscriptions on Turbo cache restore
    if (!this.subscription) {
      this.subscribeToConversation();
    }
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  // Subscribe to ConversationChannel for real-time updates
  subscribeToConversation() {
    if (!this.conversationIdValue) {
      console.warn("ConversationComposer: No conversation ID");
      return;
    }

    // Avoid duplicate subscriptions
    if (this.subscription) {
      return;
    }

    this.subscription = consumer.subscriptions.create(
      {
        channel: "ConversationChannel",
        conversation_id: this.conversationIdValue
      },
      {
        connected: () => {
          // Mark messages as read when viewing
          if (this.subscription) {
            this.subscription.perform("mark_read", {});
          }
        },
        disconnected: () => {},
        received: (data) => this.handleReceived(data)
      }
    );
  }

  // Handle incoming ActionCable messages
  handleReceived(data) {
    switch (data.type) {
    case "new_message":
      this.appendMessage(data.message);
      break;
    case "typing":
      // Could show typing indicator here
      break;
    case "status_changed":
      // Could update status badge here
      break;
    }
  }

  // Append a new message to the messages list
  appendMessage(message) {
    if (!this.hasMessagesTarget) return;

    // Check if message already exists (avoid duplicates)
    const existingMsg = this.messagesTarget.querySelector(`[data-message-id="${message.id}"]`);
    if (existingMsg) return;

    const isCustomer = message.direction === "inbound";
    const alignmentClass = isCustomer ? "items-start" : "items-end";
    const senderLabel = message.sender_name || (isCustomer ? "Customer" : (message.sender_type === "AiAgent" ? "AI Assistant" : "Agent"));
    const bgClass = isCustomer
      ? "bg-gray-50 dark:bg-gray-900"
      : "bg-emerald-50 dark:bg-emerald-900/20";

    // Inbound: show translated version (what agent sees)
    // Outbound: show original body (what agent typed)
    const rawBody = isCustomer ? (message.display_body || message.body) : message.body;
    const primaryBody = this.escapeHtml((rawBody || "").trim());

    // Build translation indicator
    let translationHtml = "";
    if (message.translated_body && message.body !== message.translated_body) {
      if (isCustomer) {
        // Inbound: show original customer message
        const langCode = message.source_language ? message.source_language.toUpperCase() : "?";
        translationHtml = `<p class="m-0 mt-2 border-t border-gray-200 pt-2 text-xs italic text-gray-500 dark:border-gray-700 dark:text-gray-400">Original (${langCode}): ${this.escapeHtml(message.body)}</p>`;
      } else {
        // Outbound: show what customer sees
        const langCode = message.target_language ? message.target_language.toUpperCase() : "?";
        translationHtml = `<p class="m-0 mt-2 border-t border-gray-200 pt-2 text-xs italic text-gray-500 dark:border-gray-700 dark:text-gray-400">Customer sees (${langCode}): ${this.escapeHtml(message.translated_body)}</p>`;
      }
    }

    // Language indicator for sender
    const langIndicator = message.source_language
      ? `<span>${message.source_language.toUpperCase()}</span>`
      : "";

    const messageHtml = `
      <div class="flex flex-col ${alignmentClass}" data-message-id="${message.id}">
        <div class="max-w-2xl w-fit rounded-lg border border-gray-200 px-4 py-3 shadow-sm dark:border-gray-700 ${bgClass}">
          <div class="mb-2 text-xs text-gray-500 dark:text-gray-400">
            <div class="font-medium text-gray-700 dark:text-gray-300">${senderLabel}</div>
            <div class="mt-0.5 flex items-center gap-2">
              ${langIndicator}
              <span>${this.formatTime(message.created_at)}</span>
            </div>
          </div>
          <p class="m-0 whitespace-pre-wrap text-sm leading-6 text-gray-900 dark:text-white">${primaryBody}</p>
          ${translationHtml}
        </div>
      </div>
    `;

    this.messagesTarget.insertAdjacentHTML("beforeend", messageHtml);
    this.scrollToBottom();
  }

  // Handle form submission success
  handleSubmitEnd(event) {
    if (event.detail.success) {
      // Clear the input
      if (this.hasInputTarget) {
        this.inputTarget.value = "";
        this.inputTarget.focus();
      }
    }
  }

  // Handle Enter key - submit on Enter, newline on Shift+Enter
  handleEnter(event) {
    if (!event.shiftKey) {
      event.preventDefault();
      const form = event.target.closest("form");
      if (form) {
        form.requestSubmit();
      }
    }
  }

  // Handle input changes (could be used for typing indicator)
  handleInput(_event) {
    // Optional: Send typing indicator
    // this.sendTypingIndicator(true)
  }

  useSuggestion(event) {
    if (!this.hasInputTarget) return;

    this.inputTarget.value = event.currentTarget.dataset.reply || "";
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
    this.inputTarget.focus();
  }

  // Send typing indicator via ActionCable
  sendTypingIndicator(isTyping) {
    if (this.subscription) {
      this.subscription.perform("typing", { is_typing: isTyping });
    }
  }

  // Scroll messages to bottom
  scrollToBottom() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
    }
  }

  // Format ISO timestamp to match server-side :short locale format
  formatTime(isoString) {
    const date = new Date(isoString);
    return date.toLocaleString([], {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  }

  // Escape HTML to prevent XSS
  escapeHtml(text) {
    if (!text) return "";
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
